import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Building2, TrendingUp, Euro, Download, Globe, ChevronRight, X, User, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import RevenueCharts from '../components/RevenueCharts';
import MonthSelector from '../components/MonthSelector';
import MobileNav from '../components/MobileNav';
import { getTranslation } from '../utils/i18n';
import Logo from '../components/Logo';

const RevenuePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('fr');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPropertyDetails, setSelectedPropertyDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) setUser(JSON.parse(userStr));
    fetchRevenue();
  }, [selectedMonth]);

  const fetchRevenue = async () => {
    try {
      const params = selectedMonth !== 'all' ? `?month=${selectedMonth}` : '';
      const response = await api.get(`/dashboard/revenue${params}`);
      setRevenueData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des revenus');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    // Simple export - in production, use jsPDF or similar
    const content = `
RAPPORT DE REVENUS - ${new Date().toLocaleDateString('fr-FR')}
================================

Vos revenus: ${revenueData?.total_concierge || 0} €
Revenus app: ${revenueData?.total_app || 0} €
Transactions: ${revenueData?.total_transactions || 0}

REVENUS PAR LOGEMENT:
${revenueData?.property_revenues?.map(item => 
  `- ${item.property_name}: ${item.revenue} €`
).join('\n')}
    `;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-revenus-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Rapport exporté avec succès');
  };

  const toggleLanguage = () => {
    setLang(lang === 'fr' ? 'en' : 'fr');
    toast.success(`Language changed to ${lang === 'fr' ? 'English' : 'Français'}`);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/auth');
  };

  const openPropertyDetails = async (propertyId) => {
    setLoadingDetails(true);
    setDetailDialogOpen(true);
    try {
      const params = selectedMonth !== 'all' ? `?month=${selectedMonth}` : '';
      const response = await api.get(`/dashboard/revenue/property/${propertyId}${params}`);
      setSelectedPropertyDetails(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 p-6 space-y-8">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
          <Logo variant="full" size="h-12" />
        </div>
        <nav className="space-y-2">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            {getTranslation(lang, 'dashboard')}
          </Button>
          <Button variant="ghost" onClick={() => navigate('/properties')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            {getTranslation(lang, 'properties')}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-rose-600 bg-rose-50 hover:bg-rose-100">
            <TrendingUp className="w-5 h-5 mr-3" strokeWidth={1.5} />
            {getTranslation(lang, 'revenue')}
          </Button>
        </nav>
        
        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={toggleLanguage}
            data-testid="toggle-language-btn"
            className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <Globe className="w-5 h-5 mr-3" strokeWidth={1.5} />
            {lang === 'fr' ? 'EN' : 'FR'}
          </Button>
        </div>
      </aside>

      <div className="lg:ml-64">
        {/* Header - Mobile optimized */}
        <header className="bg-white border-b border-slate-100 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            {/* Mobile menu */}
            <MobileNav user={user} onLogout={handleLogout} />
            
            {/* Mobile logo */}
            <div className="flex items-center lg:hidden">
              <Logo variant="full" size="h-10" />
            </div>
            
            {/* Desktop title */}
            <div className="hidden lg:block">
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {getTranslation(lang, 'myRevenue')}
              </h1>
              <p className="text-sm sm:text-base text-slate-600 mt-1">Suivez vos gains par logement</p>
            </div>
            
            {/* Actions - Hidden on very small screens */}
            <div className="hidden sm:flex items-center gap-2 sm:gap-3">
              <MonthSelector
                value={selectedMonth}
                onChange={setSelectedMonth}
              />
              <Button 
                onClick={exportToPDF}
                data-testid="export-pdf-btn"
                className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm"
              >
                <Download className="w-4 h-4 sm:mr-2" strokeWidth={1.5} />
                <span className="hidden md:inline">Export</span>
              </Button>
            </div>
            
            {/* Placeholder on mobile */}
            <div className="sm:hidden w-10"></div>
          </div>
          
          {/* Mobile title and actions */}
          <div className="lg:hidden mt-4">
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {getTranslation(lang, 'myRevenue')}
            </h1>
            <p className="text-sm text-slate-600 mt-1">Suivez vos gains</p>
            
            {/* Mobile actions row */}
            <div className="flex items-center gap-2 mt-3 sm:hidden">
              <MonthSelector
                value={selectedMonth}
                onChange={setSelectedMonth}
              />
              <Button 
                onClick={exportToPDF}
                size="sm"
                data-testid="export-pdf-mobile-btn"
                className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
              >
                <Download className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6" data-testid="total-concierge-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500 mb-1">{getTranslation(lang, 'yourRevenue')}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-rose-600 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {revenueData?.total_concierge || 0} €
                  </p>
                </div>
                <Euro className="w-8 sm:w-10 h-8 sm:h-10 text-rose-600" strokeWidth={1.5} />
              </div>
            </Card>

            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6" data-testid="total-transactions-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500 mb-1">{getTranslation(lang, 'transactions')}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-700 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {revenueData?.total_transactions || 0}
                  </p>
                </div>
                <Building2 className="w-8 sm:w-10 h-8 sm:h-10 text-slate-400" strokeWidth={1.5} />
              </div>
            </Card>
          </div>

          {/* Charts - Hidden on very small screens for better UX */}
          <div className="hidden sm:block">
            <RevenueCharts 
              propertyRevenues={revenueData?.property_revenues || []}
              totalConcierge={revenueData?.total_concierge || 0}
              hideAppRevenue={true}
            />
          </div>

          {/* Revenue by Property Table */}
          <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-medium text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {getTranslation(lang, 'revenueByProperty')}
            </h2>
            
            {revenueData?.property_revenues?.length === 0 ? (
              <div className="text-center py-8 sm:py-12" data-testid="empty-revenue-state">
                <TrendingUp className="w-10 sm:w-12 h-10 sm:h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">Aucune transaction</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3" data-testid="revenue-list">
                {revenueData?.property_revenues?.map((item) => (
                  <div 
                    key={item.property_id} 
                    onClick={() => openPropertyDetails(item.property_id)}
                    className="flex items-center justify-between p-3 sm:p-4 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer" 
                    data-testid={`revenue-item-${item.property_id}`}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <Building2 className="w-4 sm:w-5 h-4 sm:h-5 text-rose-600 flex-shrink-0" strokeWidth={1.5} />
                      <span className="font-medium text-slate-900 text-sm sm:text-base truncate">{item.property_name}</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                      <span className="text-lg sm:text-xl font-bold text-rose-600 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {item.revenue} €
                      </span>
                      <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </main>
      </div>

      {/* Dialog détails des ventes par logement */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedPropertyDetails?.property_name || 'Détails des ventes'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="py-8 text-center text-slate-500">Chargement...</div>
          ) : selectedPropertyDetails ? (
            <div className="space-y-4">
              {/* Résumé */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-rose-50 rounded-lg text-center">
                  <p className="text-xs text-rose-600 mb-1">Vos revenus</p>
                  <p className="text-lg sm:text-xl font-bold text-rose-600">{selectedPropertyDetails.total_concierge} €</p>
                </div>
                <div className="p-2 sm:p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-600 mb-1">Transactions</p>
                  <p className="text-lg sm:text-xl font-bold text-slate-700">{selectedPropertyDetails.total_transactions}</p>
                </div>
              </div>
              
              {/* Liste des ventes */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3 text-sm sm:text-base">Détail des ventes</h4>
                {selectedPropertyDetails.sales.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucune vente pour cette période</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {selectedPropertyDetails.sales.map((sale) => (
                      <div key={sale.id} className="p-2 sm:p-3 border border-slate-100 rounded-lg">
                        <div className="flex justify-between items-start mb-1 sm:mb-2">
                          <span className="font-medium text-slate-900 text-sm truncate pr-2">{sale.option_name}</span>
                          <span className="font-bold text-rose-600 text-sm flex-shrink-0">{sale.montant_concierge} €</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {sale.voyageur_nom}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(sale.date_debut).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevenuePage;
